import {
  Component,
  computed,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, map, of, switchMap } from 'rxjs';
import type { HttpError } from '@models/http-error.model';
import { AppRoutes } from '@helpers/app-routes';
import { CustomerService } from '@services/customer.service';
import { TerminalService } from '@services/terminal.service';
import { AuthService } from '@services/auth.service';
import { SessionService } from '@services/session.service';
import { SessionOrderService } from '@services/session-order.service';
import { CompanyService } from '@services/company.service';
import { MealmomentService } from '@services/meal-moment.service';
import { OrderService } from '@services/order.service';
import { PendingOrderService } from '@services/pending-order.service';
import { InviteOrderService } from '@services/invite-order.service';
import { HistoryService } from '@services/history.service';
import { UserModel } from '@models/user/user.model';
import { OrderCreateModel } from '@models/order/order.model';
import { SessionOrderCreateModel } from '@models/session-order/session-order.model';
import { ModalConfirmationComponent } from '@components/Modals/modal-confirmation/modal-confirmation.component';
import { ModalMaxOrderExceededComponent } from '@components/Modals/modal-max-order-exceeded/modal-max-order-exceeded.component';
import { LoaderComponent } from '@components/app-loader/app-loader.component';
import { SvgComponent } from '@components/svg.component';

@Component({
  selector: 'app-scan-page',
  imports: [
    FormsModule,
    TranslateModule,
    ModalConfirmationComponent,
    ModalMaxOrderExceededComponent,
    LoaderComponent,
    SvgComponent,
  ],
  templateUrl: './scan-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    class: 'block h-full',
  },
})
export class ScanPageComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly customerService = inject(CustomerService);
  private readonly terminalService = inject(TerminalService);
  private readonly authService = inject(AuthService);
  private readonly sessionService = inject(SessionService);
  private readonly sessionOrderService = inject(SessionOrderService);
  private readonly companyService = inject(CompanyService);
  private readonly mealMomentService = inject(MealmomentService);
  private readonly orderService = inject(OrderService);
  private readonly pendingOrderService = inject(PendingOrderService);
  private readonly inviteOrderService = inject(InviteOrderService);
  private readonly historyService = inject(HistoryService);
  private readonly toastr = inject(ToastrService);
  private readonly translate = inject(TranslateService);

  @ViewChild('searchInput')
  private readonly searchInput?: ElementRef<HTMLInputElement>;
  #autoNavigateTimeout: ReturnType<typeof setTimeout> | null = null;
  #pendingMaxOrderCustomer: UserModel | null = null;

  searchQuery = '';
  readonly isLoading = signal(false);
  readonly searchResults = signal<UserModel[]>([]);
  readonly showPassageValidatedModal = signal(false);
  readonly validatedCustomerName = signal('');
  readonly showMaxOrderExceededModal = signal(false);
  readonly maxOrderCurrentCount = signal(0);
  readonly maxOrderLimit = signal(0);
  readonly countdownSeconds = 3;

  protected readonly isCheckinMode = computed(
    () => this.terminalService.currentTerminal()?.is_check_in ?? false,
  );

  ngOnInit(): void {
    const terminal = this.terminalService.currentTerminal();
    if (!terminal) {
      return;
    }

    this.terminalService.loadTerminalById(terminal.id).subscribe({
      next: () => this.#initCheckinMode(),
      error: () => {
        this.authService.logout();
        void this.router.navigate([AppRoutes.login]);
        this.toastr.error(
          this.translate.instant('scan_page.errors.terminal_refresh_error_message'),
          this.translate.instant('scan_page.errors.terminal_refresh_error_title'),
        );
      },
    });
  }

  #initCheckinMode(): void {
    const terminal = this.terminalService.currentTerminal();
    if (!terminal) {
      return;
    }

    if (!this.isCheckinMode()) {
      return;
    }

    if (this.authService.hasCheckInSession()) {
      this.authService.logout();
      return;
    }

    this.terminalService.checkInToken(terminal.id).subscribe({
      next: (jwt) => {
        this.authService.storeCheckInTokens(jwt.token, jwt.refresh_token);
        this.authService.logout();
      },
      error: () => {
        this.authService.logout();
        this.toastr.error(
          this.translate.instant('scan_page.errors.checkin_token_error_message'),
          this.translate.instant('scan_page.errors.checkin_token_error_title'),
        );
      },
    });
  }

  ngOnDestroy(): void {
    if (this.#autoNavigateTimeout !== null) {
      clearTimeout(this.#autoNavigateTimeout);
    }
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => {
      this.searchInput?.nativeElement.focus();
    });
  }

  onSearchInputBlur(event: FocusEvent): void {
    const nextFocusTarget = event.relatedTarget as HTMLElement | null;
    if (nextFocusTarget?.closest('input, textarea, select, [contenteditable="true"]')) {
      return;
    }

    queueMicrotask(() => {
      this.searchInput?.nativeElement.focus();
    });
  }

  onPageMouseDown(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;

    if (!target) {
      return;
    }

    if (target.closest('input, textarea, select, [contenteditable="true"]')) {
      return;
    }

    event.preventDefault();
  }

  onSearch(): void {
    if (!this.searchQuery.trim() || this.isLoading()) {
      return;
    }

    this.isLoading.set(true);
    this.searchResults.set([]);

    this.customerService.searchCustomer(this.searchQuery.trim()).subscribe({
      next: (customers) => {
        this.isLoading.set(false);

        if (customers.length === 0) {
          this.searchQuery = '';
          this.toastr.error(
            this.translate.instant('scan_page.errors.not_found_message'),
            this.translate.instant('scan_page.errors.not_found_title'),
          );
          return;
        }

        if (customers.length === 1) {
          this.selectCustomer(customers[0]);
          return;
        }

        this.searchResults.set(customers);
      },
      error: (err: HttpError) => {
        this.isLoading.set(false);
        this.searchQuery = '';
        this.toastr.error(
          this.translate.instant('scan_page.' + err.message),
          this.translate.instant('scan_page.errors.search_error_title'),
        );
      },
    });
  }

  selectCustomer(customer: UserModel): void {
    this.customerService.selectCustomer(customer);
    this.searchResults.set([]);
    this.searchQuery = '';

    if (this.isCheckinMode()) {
      this.#validateReducedPassage(customer);
      return;
    }

    this.#checkMaxOrder(customer);
  }

  onMaxOrderBackToSelection(): void {
    this.showMaxOrderExceededModal.set(false);
    this.#pendingMaxOrderCustomer = null;
    this.cancelSelection();
  }

  onMaxOrderContinue(): void {
    this.showMaxOrderExceededModal.set(false);
    const customer = this.#pendingMaxOrderCustomer;
    this.#pendingMaxOrderCustomer = null;
    if (customer) {
      this.#proceedToOrder(customer);
    }
  }

  #checkMaxOrder(customer: UserModel): void {
    this.isLoading.set(true);

    forkJoin([this.companyService.getCompany(), this.mealMomentService.getMealmoments()])
      .pipe(
        switchMap(() => {
          const maxOrder = this.companyService.currentCompany()?.max_order ?? null;
          const mealMoment = this.mealMomentService.currentMealMoment();

          if (maxOrder === null || maxOrder <= 0 || !mealMoment) {
            return of(null);
          }

          const deliveryDate = new Date().toISOString().split('T')[0];
          return this.sessionOrderService
            .getOrderCount(customer.id, mealMoment.id, deliveryDate)
            .pipe(map((count) => ({ count, maxOrder })));
        }),
      )
      .subscribe({
        next: (result) => {
          this.isLoading.set(false);

          if (result && result.count >= result.maxOrder) {
            this.#pendingMaxOrderCustomer = customer;
            this.maxOrderCurrentCount.set(result.count);
            this.maxOrderLimit.set(result.maxOrder);
            this.showMaxOrderExceededModal.set(true);
            return;
          }

          this.#proceedToOrder(customer);
        },
        error: () => {
          this.isLoading.set(false);
          this.#proceedToOrder(customer);
        },
      });
  }

  #proceedToOrder(customer: UserModel): void {
    const pendingOrder = this.pendingOrderService.consume(customer.id);
    if (pendingOrder) {
      this.inviteOrderService.restoreHostCart(pendingOrder.order, pendingOrder.mealPlan);
      this.inviteOrderService.restoreParticipant({
        cart: pendingOrder.order,
        mealPlan: pendingOrder.mealPlan,
      });
      this.historyService.pushPath(`/${AppRoutes.chooseOrderType}`);
      this.historyService.pushPath(`/${AppRoutes.order}`);
      void this.router.navigate([AppRoutes.orderSummary]);
      return;
    }

    this.orderService.clear();
    this.inviteOrderService.reset();
    void this.router.navigate([AppRoutes.chooseOrderType]);
  }

  cancelSelection(): void {
    this.searchResults.set([]);
    this.searchQuery = '';
    queueMicrotask(() => {
      this.searchInput?.nativeElement.focus();
    });
  }

  onModalButtonClick(): void {
    this.#closePassageValidatedModal();
  }

  #closePassageValidatedModal(): void {
    if (this.#autoNavigateTimeout !== null) {
      clearTimeout(this.#autoNavigateTimeout);
      this.#autoNavigateTimeout = null;
    }
    this.showPassageValidatedModal.set(false);
    queueMicrotask(() => {
      this.searchInput?.nativeElement.focus();
    });
  }

  #validateReducedPassage(customer: UserModel): void {
    const terminal = this.terminalService.currentTerminal();
    const company = this.companyService.currentCompany();
    const mealMoment = this.mealMomentService.currentMealMoment();

    if (!terminal || !company || !mealMoment) {
      this.toastr.error(
        this.translate.instant('scan_page.errors.reduced_order_error_message'),
        this.translate.instant('scan_page.errors.reduced_order_error_title'),
      );
      return;
    }

    this.isLoading.set(true);

    this.sessionService
      .getLastSession()
      .pipe(
        switchMap((session) => {
          const order: OrderCreateModel = {
            admission_id: null,
            user_id: customer.id,
            enterprise_id: terminal.enterprise_id,
            mealmoment_id: mealMoment.id,
            company_id: company.id,
            delivery_date: new Date().toISOString().split('T')[0],
            number_of_admission: 1,
            payment_ways: [],
            is_order_for_group: false,
            service_id: null,
            order_items: [],
            comment: null,
            invites: [],
          };

          const sessionOrder: SessionOrderCreateModel = {
            self_pos_terminal_session_id: session.id,
            user_id: customer.id,
            order,
            is_validated: true,
            is_forced: false,
            forced_comment: null,
          };

          return this.sessionOrderService.createSessionOrder(sessionOrder);
        }),
      )
      .subscribe({
        next: () => {
          this.isLoading.set(false);
          this.searchQuery = '';
          this.validatedCustomerName.set(this.getCustomerDisplayName(customer));
          this.showPassageValidatedModal.set(true);
          this.#autoNavigateTimeout = setTimeout(
            () => this.#closePassageValidatedModal(),
            this.countdownSeconds * 1000,
          );
        },
        error: () => {
          this.isLoading.set(false);
          this.toastr.error(
            this.translate.instant('scan_page.errors.reduced_order_error_message'),
            this.translate.instant('scan_page.errors.reduced_order_error_title'),
          );
        },
      });
  }

  protected getCustomerDisplayName(customer: UserModel): string {
    const fullName = `${customer.firstname ?? ''} ${customer.lastname ?? ''}`.trim();
    return fullName || customer.username;
  }
}
